import { Type } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { ChecklistToggleRow } from '@models/checklist-toggle/checklist-toggle.model';

import {
  listInvoicesForReconciliationQuery,
  listVendorEstimateSummariesQuery,
} from '@shared/services/gql.service';
import { Workflow } from '@shared/store/workflow/workflow.store';

import { QuarterDate } from '../../../period-close.component';
import { ChecklistRowInfoComponent } from '../components/checklist-row-info/checklist-row-info.component';
import { ChecklistSectionDiscountComponent } from '../components/checklist-section-discount/checklist-section-discount.component';
import { ChecklistSectionGatherContractsComponent } from '../components/checklist-section-gather-contracts/checklist-section-gather-contracts.component';
import { ChecklistSectionGatherEstimatesComponent } from '../components/checklist-section-gather-estimates/checklist-section-gather-estimates.component';
import { ChecklistSectionGatherPatientsComponent } from '../components/checklist-section-gather-patients/checklist-section-gather-patients.component';

export enum QuarterCloseChecklistSection {
  CloseMonth = 'CloseMonth',
  ConfirmClose = 'ConfirmClose',
  ConfirmForecast = 'ConfirmForecast',
  GatherDocuments = 'GatherDocuments',
}

export enum QuarterCloseChecklistSectionTitles {
  ConfirmClose = 'CONFIRM & CLOSE',
  ConfirmForecast = 'CONFIRM FORECAST SETTINGS',
  GatherDocuments = 'GATHER DOCUMENTS',
}

export type ChecklistComponentAddPoReportUploadFn = () => void;

export type ChecklistComponentChangeLockFn = (
  locked: boolean,
  workflow: Workflow,
  isAssign: boolean
) => Promise<void>;

export interface ChecklistComponentData {
  data: unknown;
  id: string;
  parent: ChecklistRowInfoComponent;
}

export interface ChecklistComponentDataAddPoReport {
  addPoReportUploadClick: ChecklistComponentAddPoReportUploadFn;
  disabledLock: boolean;
}

export interface ChecklistComponentDataGatherEstimates {
  disabledLock: boolean;
  vendorEstimateSummaries: QuarterCloseChecklistVendorEstimateSummary[];
  vendorEstimateUploadClick: ChecklistComponentVendorEstimateUploadFn;
}

export type ChecklistComponentVendorEstimateUploadFn = () => void;

export type ChecklistInvoiceExpenseAmounts = listInvoicesForReconciliationQuery['expense_amounts'];

export type ChecklistInvoiceMonthlyQuickView = {
  amountInvoiced: string;
  numberOfInvoices: string;
};

export interface ChecklistMonthlyDropdownDate extends QuarterDate {
  disabled: boolean;
  label: string;
  value: string;
}

export interface QuarterCloseChecklistPermissionList {
  userHasChecklistAdminPermission: boolean;
  userHasChecklistContractsPermission: boolean;
  userHasChecklistDiscountsPermission: boolean;
  userHasChecklistForecastMethodologyPermission: boolean;
  userHasChecklistInvoicesPermission: boolean;
  userHasChecklistPatientDataPermission: boolean;
  userHasChecklistPatientSiteCurvePermission: boolean;
  userHasChecklistTrialTimelinePermission: boolean;
  userHasChecklistVendorEstimatesPermission: boolean;
  userHasChecklistVendorExpensesPermission: boolean;
}

export type QuarterCloseChecklistRowComponent =
  | ChecklistSectionDiscountComponent
  | ChecklistSectionGatherContractsComponent
  | ChecklistSectionGatherEstimatesComponent
  | ChecklistSectionGatherPatientsComponent;

export type QuarterCloseChecklistRowComponents = {
  CloseDiscounts: ChecklistComponent<ChecklistSectionDiscountComponent>;
  GatherContracts: ChecklistComponent<ChecklistSectionGatherContractsComponent>;
  GatherEstimates: ChecklistComponent<ChecklistSectionGatherEstimatesComponent>;
  GatherPatients: ChecklistComponent<ChecklistSectionGatherPatientsComponent>;
};

export type QuarterCloseChecklistRowToggle = {
  -readonly [key in keyof typeof ChecklistToggleRow]: BehaviorSubject<boolean>;
};

export interface QuarterCloseChecklistVendorEstimateQuery {
  data: listVendorEstimateSummariesQuery[] | null;
  errors: string[];
  success: boolean;
}

export interface QuarterCloseChecklistVendorEstimateSummary {
  activitiesExceedingAmountRemaining: string[];
  activitiesExceedingForecast: string[];
  id: string;
  name: string;
  vendorEstimateExists: boolean;
}

export class ChecklistComponent<T> {
  // eslint-disable-next-line no-useless-constructor
  constructor(
    public component: Type<T>,
    public data: unknown,
    public id: string
  ) {}
}
